import { useState, useEffect, useRef } from 'react';

import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { portfolioItems } from '@/mocks/portfolio';

const filters = [
  { key: 'grafico', label: 'Diseño Gráfico' },
  { key: 'impresa', label: 'Publicidad Impresa' },
  { key: 'redes', label: 'Contenido Digital' },
  { key: 'video', label: 'Videos' },
  { key: 'empaques', label: 'Empaques' },
  { key: 'web', label: 'Páginas Web' },
];

const webProjects = [
  {
    id: 'web-1',
    name: 'Lion Urban Design & Print',
    description: 'Sitio web corporativo para empresa de diseño gráfico, impresión y publicidad. Diseño moderno con identidad visual sólida, portafolio de servicios y presencia digital de alto impacto.',
    url: 'https://www.lionurbandesignprint.com',
    screenshot: 'https://static.readdy.ai/image/7b80f9c748a6c492c1a0f4fbaf2d66d7/aacf68ed8932831ac26dfb046a5826d9.png',
    tags: ['Corporativo', 'Diseño Gráfico', 'Publicidad'],
    color: '#FF6B35',
    domain: 'lionurbandesignprint.com',
  },
  {
    id: 'web-2',
    name: 'Próximo proyecto',
    description: '¿Quieres que el siguiente sea el tuyo? Contáctame y construimos tu presencia digital juntos.',
    url: 'https://wa.me/573024819377?text=Hola%20Michael%2C%20quiero%20una%20p%C3%A1gina%20web',
    screenshot: null,
    tags: ['En proceso', 'Disponible'],
    color: '#06FFA5',
    domain: null,
    isPlaceholder: true,
  },
];

type PortfolioItem = {
  id: number;
  title: string;
  category: string;
  categoryLabel: string;
  description: string;
  image: string;
  orientation: string;
  videoUrl?: string;
  videoType?: string;
};

type LightboxItem = {
  image: string;
  title: string;
  description: string;
  videoUrl?: string;
  videoType?: string;
  orientation?: string;
};

function ReelCard({ item, onOpen }: { item: PortfolioItem; onOpen: (item: LightboxItem) => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hovered, setHovered] = useState(false);
  const hasVideo = item.videoUrl && item.videoType === 'mp4';

  return (
    <div
      className="relative rounded-2xl overflow-hidden cursor-pointer group bg-[#111]"
      style={{ aspectRatio: '9/16' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onOpen({
        image: item.image,
        title: item.title,
        description: item.description,
        videoUrl: item.videoUrl,
        videoType: item.videoType,
        orientation: 'reel',
      })}
    >
      {hasVideo ? (
        <video
          ref={videoRef}
          src={item.videoUrl}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : item.image ? (
        <img
          src={item.image}
          alt={item.title}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-b from-[#1a1a1a] to-[#0d0d0d]">
          <div className="w-14 h-14 flex items-center justify-center rounded-full border border-dashed border-white/20">
            <i className="ri-video-add-line text-white/30 text-2xl"></i>
          </div>
          <span className="text-white/20 text-xs font-poppins tracking-wide">Próximamente</span>
        </div>
      )}
      <div className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${hovered ? 'opacity-100' : 'opacity-0'}`}></div>
      {!hasVideo && item.image && (
        <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${hovered ? 'opacity-0' : 'opacity-100'}`}>
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/15 backdrop-blur-sm border border-white/30">
            <i className="ri-play-fill text-white text-xl ml-1"></i>
          </div>
        </div>
      )}
      <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${hovered ? 'opacity-100' : 'opacity-0'}`}>
        <div className="flex items-center gap-2 bg-[#FF6B35]/90 backdrop-blur-sm text-white text-sm font-medium px-4 py-2 rounded-full">
          <i className="ri-fullscreen-line text-sm"></i>
          <span>Ver en pantalla completa</span>
        </div>
      </div>
      <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm rounded-full px-2.5 py-1">
        <i className="ri-instagram-line text-[#FF6B35] text-xs"></i>
        <span className="text-white/80 text-[10px] font-medium">Reel</span>
      </div>
    </div>
  );
}

export default function Portfolio() {
  const [activeFilter, setActiveFilter] = useState('grafico');
  const [lightbox, setLightbox] = useState<LightboxItem | null>(null);
  const [renderKey, setRenderKey] = useState(0);
  const ref = useScrollAnimation();

  useEffect(() => {
    const handler = (e: Event) => {
      const custom = e as CustomEvent<string>;
      setActiveFilter(custom.detail);
      setRenderKey((k) => k + 1);
    };
    window.addEventListener('portfolio-filter', handler);
    return () => window.removeEventListener('portfolio-filter', handler);
  }, []);

  const handleFilterChange = (key: string) => {
    setActiveFilter(key);
    setRenderKey((k) => k + 1);
  };

  const filtered = portfolioItems.filter((item) => item.category === activeFilter);
  const openLightbox = (item: LightboxItem) => setLightbox(item);
  const closeLightbox = () => setLightbox(null);

  const renderPortraitCard = (item: typeof filtered[0]) => (
    <div
      key={item.id}
      className="portfolio-card relative rounded-2xl overflow-hidden cursor-pointer group"
      style={{ aspectRatio: '3/4' }}
      onClick={() => openLightbox({
        image: item.image,
        title: item.title,
        description: item.description,
        videoUrl: item.category === 'video' ? item.videoUrl : undefined,
        videoType: item.category === 'video' ? (item as any).videoType : undefined,
      })}
    >
      <img
        src={item.image}
        alt={item.title}
        className="w-full h-full object-cover object-top"
      />
      <div className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:rotate-12">
        <i className="ri-zoom-in-line text-white text-sm"></i>
      </div>
    </div>
  );

  return (
    <section id="portfolio" className="py-24 md:py-32 relative bg-[#080808] overflow-hidden">
      <div ref={ref} className="relative z-[3] max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="fade-in-up flex items-center gap-4 mb-6">
          <span className="text-[#FF6B35] font-bebas text-lg tracking-widest">04</span>
          <div className="w-12 h-px bg-[#FF6B35]"></div>
          <span className="text-white/40 text-sm tracking-widest uppercase">Portafolio</span>
        </div>

        <div className="fade-in-up flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <h2 className="font-bebas text-5xl md:text-6xl lg:text-7xl text-white leading-tight">
            MI <span className="gradient-text">TRABAJO</span>
          </h2>
          <p className="text-white/50 font-poppins text-sm max-w-xs">
            Proyectos reales que demuestran creatividad, técnica y resultados.
          </p>
        </div>

        {/* Filters */}
        <div className="fade-in-up flex flex-wrap gap-3 mb-10">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => handleFilterChange(f.key)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeFilter === f.key
                  ? 'bg-[#FF6B35] text-white'
                  : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 hover:text-white'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Web Projects View */}
        {activeFilter === 'web' ? (
          <div key={`web-${renderKey}`}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {webProjects.map((project) => (
                project.isPlaceholder ? (
                  <a
                    key={project.id}
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative flex flex-col items-center justify-center rounded-2xl border border-dashed border-white/10 hover:border-[#06FFA5]/40 transition-all duration-300 cursor-pointer overflow-hidden"
                    style={{ minHeight: '380px' }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-[#06FFA5]/3 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative z-10 flex flex-col items-center gap-4 p-10 text-center">
                      <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#06FFA5]/10 group-hover:bg-[#06FFA5]/20 transition-colors duration-300">
                        <i className="ri-add-line text-[#06FFA5] text-2xl"></i>
                      </div>
                      <h4 className="font-poppins font-bold text-white text-xl">{project.name}</h4>
                      <p className="text-white/50 text-sm leading-relaxed max-w-xs font-poppins">{project.description}</p>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {project.tags.map((tag) => (
                          <span key={tag} className="text-xs px-3 py-1 rounded-full font-medium" style={{ backgroundColor: `${project.color}18`, color: project.color }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 text-[#06FFA5] text-sm font-medium mt-2 group-hover:gap-3 transition-all duration-300">
                        <i className="ri-whatsapp-line"></i>
                        <span>Contáctame por WhatsApp</span>
                        <i className="ri-arrow-right-line"></i>
                      </div>
                    </div>
                  </a>
                ) : (
                  <div
                    key={project.id}
                    className="group relative rounded-2xl overflow-hidden border border-white/5 hover:border-[#FF6B35]/30 transition-all duration-300 cursor-pointer"
                    style={{ minHeight: '380px' }}
                    onClick={() => window.open(project.url, '_blank')}
                  >
                    <div className="relative w-full overflow-hidden" style={{ height: '260px' }}>
                      <img
                        src={project.screenshot ?? ''}
                        alt={project.name}
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute top-0 left-0 right-0 h-8 bg-[#1a1a1a] flex items-center px-3 gap-2 z-10">
                        <div className="flex gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]"></div>
                          <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]"></div>
                          <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]"></div>
                        </div>
                        <div className="flex-1 mx-3 h-4 bg-[#2a2a2a] rounded-full flex items-center px-3">
                          <span className="text-white/30 text-[9px] font-poppins truncate">{project.domain}</span>
                        </div>
                        <i className="ri-external-link-line text-white/30 text-xs"></i>
                      </div>
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center z-20">
                        <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm px-5 py-2.5 rounded-full">
                          <i className="ri-external-link-line"></i>
                          <span>Visitar sitio</span>
                        </div>
                      </div>
                    </div>
                    <div className="p-6 bg-[#0d0d0d]">
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <h4 className="font-poppins font-bold text-white text-lg">{project.name}</h4>
                        <div className="flex flex-wrap gap-2">
                          {project.tags.map((tag) => (
                            <span key={tag} className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: `${project.color}18`, color: project.color }}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <p className="text-white/50 text-sm leading-relaxed font-poppins">{project.description}</p>
                    </div>
                  </div>
                )
              ))}
            </div>
            <p className="text-white/25 text-xs font-poppins text-center mt-10">
              Esta sección se actualiza con cada nuevo proyecto web completado
            </p>
          </div>
        ) : activeFilter === 'video' ? (
          <div key={`video-${renderKey}`} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filtered.map((item) => (
              <ReelCard key={item.id} item={item as PortfolioItem} onOpen={openLightbox} />
            ))}
          </div>
        ) : (
          /* Uniform portrait grid — all cards vertical 3/4 */
          <div key={`grid-${renderKey}`} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {filtered.map(renderPortraitCard)}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4"
          onClick={closeLightbox}
        >
          <div
            className={`relative rounded-2xl overflow-hidden ${lightbox.orientation === 'reel' ? 'max-w-sm w-full' : 'max-w-4xl w-full'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-black/60 backdrop-blur-sm rounded-full text-white hover:bg-black/80 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>

            {lightbox.videoUrl ? (
              lightbox.videoType === 'mp4' ? (
                <div className="relative w-full bg-black">
                  <video
                    src={lightbox.videoUrl}
                    controls
                    autoPlay
                    playsInline
                    className={`w-full object-contain ${lightbox.orientation === 'reel' ? 'max-h-[85vh]' : 'max-h-[80vh]'}`}
                    style={{ display: 'block' }}
                  >
                    Tu navegador no soporta la reproducción de video.
                  </video>
                </div>
              ) : (
                <div className="relative w-full bg-black" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    src={lightbox.videoUrl}
                    title={lightbox.title}
                    className="absolute inset-0 w-full h-full"
                    allow="autoplay; fullscreen"
                    allowFullScreen
                  ></iframe>
                </div>
              )
            ) : (
              <img
                src={lightbox.image}
                alt={lightbox.title}
                className="w-full h-auto max-h-[80vh] object-contain bg-[#0d0d0d]"
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
}
